<template>
  <div>
    <!-- Edit mode: draggable custom tab bar -->
    <div v-if="editMode" class="room-tabs-bar room-custom-tabs d-flex align-center">
      <draggable
        v-model="localRooms"
        tag="div"
        class="d-flex align-center custom-tabs-list"
        handle=".drag-handle"
        :animation="150"
        ghost-class="tab-ghost"
        chosen-class="tab-chosen"
        @end="onDragEnd"
      >
        <div
          v-for="room in localRooms"
          :key="room.id"
          class="custom-tab"
          :class="{ 'custom-tab--active': room.id === activeRoomId }"
          @click="onTabClick(room.id)"
        >
          <v-icon x-small class="drag-handle mr-1" color="#94A3B8">mdi-drag</v-icon>
          <span>{{ room.name }}</span>
          <v-btn icon x-small class="ml-1 tab-action-btn" @click.stop="editRoom(room)">
            <v-icon x-small color="#3989C9">mdi-pencil</v-icon>
          </v-btn>
          <v-btn v-if="localRooms.length > 1" icon x-small class="ml-1 tab-action-btn-danger" @click.stop="deleteRoom(room)">
            <v-icon x-small color="#F44336">mdi-close</v-icon>
          </v-btn>
        </div>
      </draggable>

      <div class="custom-tab add-room-tab" @click="addRoom">
        <v-icon color="#00B3BE" small>mdi-plus</v-icon>
      </div>

      <div class="flex-grow-1"></div>
    </div>

    <!-- User mode: original v-tabs -->
    <v-tabs
      v-else
      v-model="activeTab"
      background-color="rgba(245, 245, 245, 0.5)"
      color="#3989C9"
      slider-color="#00B3BE"
      class="room-tabs-bar"
      @change="onTabChange"
    >
      <v-tab
        v-for="(room, index) in rooms"
        :key="room.id"
        :href="`#${room.id}`"
        style="position: relative"
      >
        {{ room.name }}
        <v-chip
          v-if="keyboardShortcutsEnabled && (showShortcutBadges || showBadgesTemporarily)"
          x-small
          color="white"
          text-color="primary"
          style="position: absolute; bottom: 4px; right: 4px; font-weight: bold; min-width: 20px"
        >
          {{ getRoomShortcut(room.name, index) }}
        </v-chip>
      </v-tab>

      <v-spacer></v-spacer>

      <div class="d-flex align-center px-4">
        <v-btn icon small @click="$emit('close-floor-plan')" class="mr-3 zoom-control-btn-danger">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-btn v-if="Math.round(zoom * 100) !== 100" icon small @click="$emit('reset-view')" class="mr-2 zoom-control-btn">
          <v-icon>mdi-restore</v-icon>
        </v-btn>
        <v-btn icon small @click="$emit('zoom-out')" class="zoom-control-btn">
          <v-icon>mdi-minus</v-icon>
        </v-btn>
        <span class="mx-2 text-caption zoom-label">{{ Math.round(zoom * 100) }}%</span>
        <v-btn icon small @click="$emit('zoom-in')" class="zoom-control-btn">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </div>
    </v-tabs>

    <!-- Add/Edit Room Dialog -->
    <v-dialog v-model="roomDialog" max-width="500px" persistent>
      <v-card class="room-dialog-card">
        <v-card-title class="room-dialog-title">
          <v-icon left color="#3989C9">mdi-door-open</v-icon>
          <span class="dialog-title-text">{{ dialogTitle }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-6">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedRoom.name"
                  :label="$t('$vuetify.floorPlan.roomName')"
                  :rules="[rules.req]"
                  outlined
                  dense
                  class="room-input"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="editedRoom.width"
                  :label="$t('$vuetify.floorPlan.width')"
                  :rules="[rules.req, rules.gt(0)]"
                  type="number"
                  outlined
                  dense
                  suffix="px"
                  class="room-input"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="editedRoom.height"
                  :label="$t('$vuetify.floorPlan.height')"
                  :rules="[rules.req, rules.gt(0)]"
                  type="number"
                  outlined
                  dense
                  suffix="px"
                  class="room-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn class="cancel-btn" elevation="0" @click="closeDialog">
            {{ $t('$vuetify.cancel') }}
          </v-btn>
          <v-btn
            class="save-btn"
            elevation="0"
            @click="saveRoom"
            :disabled="!isValidRoom"
          >
            <v-icon left small>mdi-content-save</v-icon>
            {{ $t('$vuetify.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import rules from '@/plugins/rules'
import draggable from 'vuedraggable'

export default {
  name: 'RoomTabsBar',
  components: { draggable },
  inject: ['confirm', 'confirmClose'],
  props: {
    rooms: {
      type: Array,
      required: true
    },
    activeRoomId: {
      type: String,
      default: null
    },
    editMode: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: 1
    },
    keyboardShortcutsEnabled: {
      type: Boolean,
      default: false
    },
    showShortcutBadges: {
      type: Boolean,
      default: false
    },
    showBadgesTemporarily: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: null,
      localRooms: [],
      roomDialog: false,
      editedRoom: {
        id: null,
        name: '',
        width: 1200,
        height: 800,
        order: 0
      },
      defaultRoom: {
        id: null,
        name: '',
        width: 1200,
        height: 800,
        order: 0
      },
      editIndex: -1,
      rules: {
        req: rules.req(),
        gt: rules.gt
      }
    }
  },
  computed: {
    dialogTitle () {
      return this.editIndex === -1
        ? this.$t('$vuetify.floorPlan.addRoom')
        : this.$t('$vuetify.floorPlan.editRoom')
    },
    isValidRoom () {
      return (
        this.editedRoom.name &&
        this.editedRoom.name.trim() !== '' &&
        this.editedRoom.width > 0 &&
        this.editedRoom.height > 0
      )
    }
  },
  watch: {
    activeRoomId: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.activeTab = newVal
        }
      }
    },
    rooms: {
      immediate: true,
      handler (newVal) {
        this.localRooms = [...newVal]
      }
    }
  },
  methods: {
    /**
     * Normalize Croatian characters to English
     */
    normalizeCharacters (text) {
      if (!text) return text
      const charMap = {
        š: 's',
        Š: 'S',
        č: 'c',
        Č: 'C',
        ć: 'c',
        Ć: 'C',
        ž: 'z',
        Ž: 'Z',
        đ: 'd',
        Đ: 'D'
      }
      return text.replace(/[šŠčČćĆžŽđĐ]/g, char => charMap[char] || char)
    },

    /**
     * Get room shortcut (first letter of room name)
     */
    getRoomShortcut (roomName, index) {
      const normalized = this.normalizeCharacters(roomName || '')
      let letter = normalized[0] ? normalized[0].toUpperCase() : String.fromCharCode(65 + index)

      // Check for duplicates
      const roomShortcuts = {}
      this.rooms.forEach((room, idx) => {
        if (idx <= index) {
          const norm = this.normalizeCharacters(room.name || '')
          const l = norm[0] ? norm[0].toUpperCase() : String.fromCharCode(65 + idx)
          roomShortcuts[l] = (roomShortcuts[l] || 0) + 1

          if (idx === index && roomShortcuts[l] > 1) {
            letter = l + (roomShortcuts[l] - 1)
          }
        }
      })

      return letter
    },

    handleRoomKeyPress (e) {
      // Ignore if keyboard shortcuts are disabled
      if (!this.keyboardShortcutsEnabled) return

      // Ignore if in edit mode
      if (this.editMode) return

      // Ignore if user is typing in an input field
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return

      const key = e.key

      // Handle letters for room selection
      if (/^[a-zA-Z]$/.test(key)) {
        const letter = key.toUpperCase()

        // Find room with matching shortcut
        const roomIndex = this.rooms.findIndex((room, idx) => {
          return this.getRoomShortcut(room.name, idx) === letter
        })

        if (roomIndex >= 0) {
          // Prevent event from bubbling to other components
          e.preventDefault()
          e.stopPropagation()

          const room = this.rooms[roomIndex]
          this.onTabChange(room.id)
        }
      }
    },
    onTabClick (roomId) {
      if (roomId !== this.activeRoomId) {
        this.$emit('room-changed', roomId)
      }
    },
    onDragEnd () {
      const reordered = this.localRooms.map((room, index) => ({
        ...room,
        order: index + 1
      }))
      this.$emit('rooms-reordered', reordered)
    },
    onTabChange (tabId) {
      // v-tabs can send numeric index when clicking on tabs without href
      // We only want to emit room changes for actual room IDs (UUIDs/strings)
      if (tabId && typeof tabId === 'string' && tabId !== this.activeRoomId) {
        this.$emit('room-changed', tabId)
      }
    },
    addRoom () {
      this.editedRoom = {
        ...this.defaultRoom,
        id: uuidv4(),
        name: this.$t('$vuetify.floorPlan.room') + ' ' + (this.rooms.length + 1),
        order: this.rooms.length + 1
      }
      this.editIndex = -1
      this.roomDialog = true
    },
    editRoom (room) {
      this.editIndex = this.rooms.findIndex(r => r.id === room.id)
      this.editedRoom = { ...room }
      this.roomDialog = true
    },
    deleteRoom (room) {
      this.confirm({
        title: this.$t('$vuetify.floorPlan.deleteRoom'),
        message: this.$t('$vuetify.floorPlan.deleteRoomConfirm', { name: room.name }),
        options: {
          toolbar: true,
          width: 410,
          confirmText: this.$t('$vuetify.yes'),
          cancelText: this.$t('$vuetify.no')
        }
      }).then((resp) => {
        if (resp) {
          this.$emit('room-deleted', room.id)
        }
        this.confirmClose()
      })
    },
    saveRoom () {
      if (!this.isValidRoom) return

      if (this.editIndex === -1) {
        // Add new room
        this.$emit('room-added', {
          ...this.editedRoom,
          active: true,
          objects: [],
          backgroundPattern: 'none',
          backgroundColor: '#f5f5f5'
        })
      } else {
        // Update existing room
        this.$emit('room-updated', this.editedRoom)
      }

      this.closeDialog()
    },
    closeDialog () {
      this.roomDialog = false
      this.editedRoom = { ...this.defaultRoom }
      this.editIndex = -1
    }
  },
  mounted () {
    // Register keyboard listener for room shortcuts
    document.addEventListener('keydown', this.handleRoomKeyPress)
  },
  beforeDestroy () {
    // Clean up keyboard listener
    document.removeEventListener('keydown', this.handleRoomKeyPress)
  }
}
</script>

<style scoped>
/* Custom draggable tab bar (edit mode) */
.room-custom-tabs {
  min-height: 48px;
  padding: 0 4px;
}

.custom-tabs-list {
  overflow-x: auto;
}

.custom-tab {
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  height: 48px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: rgba(57, 137, 201, 0.7);
  border-bottom: 2px solid transparent;
  transition: color 0.2s ease, background 0.2s ease;
  user-select: none;
  white-space: nowrap;
}

.custom-tab:hover {
  color: rgba(57, 137, 201, 1);
  background: rgba(57, 137, 201, 0.05);
}

.custom-tab--active {
  color: #3989C9;
  font-weight: 700;
  border-bottom-color: #00B3BE;
}

.drag-handle {
  cursor: grab !important;
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.custom-tab:hover .drag-handle {
  opacity: 0.9;
}

.tab-ghost {
  opacity: 0.35;
  background: rgba(57, 137, 201, 0.1) !important;
  border-radius: 4px;
}

.tab-chosen {
  background: rgba(57, 137, 201, 0.06);
}

.add-room-tab {
  min-width: 48px;
  justify-content: center;
  border-bottom: none;
}

/* Room Tabs Bar Glass Style */
.room-tabs-bar {
  backdrop-filter: blur(15px) !important;
  -webkit-backdrop-filter: blur(15px) !important;
  border-bottom: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 2px 12px rgba(0, 179, 190, 0.08) !important;
}

/* Tab Styling */
.v-tab {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  color: rgba(57, 137, 201, 0.7) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.v-tab:hover {
  color: rgba(57, 137, 201, 1) !important;
  background: rgba(57, 137, 201, 0.05) !important;
}

.v-tab--active {
  color: #3989C9 !important;
  font-weight: 700 !important;
}

/* Add Room Tab */
.add-room-tab {
  min-width: 60px !important;
}

.add-room-tab:hover {
  background: rgba(0, 179, 190, 0.08) !important;
}

/* Tab Action Buttons */
.tab-action-btn {
  transition: all 0.2s ease !important;
}

.tab-action-btn:hover {
  background: rgba(57, 137, 201, 0.15) !important;
  transform: scale(1.1);
}

.tab-action-btn-danger {
  transition: all 0.2s ease !important;
}

.tab-action-btn-danger:hover {
  background: rgba(244, 67, 54, 0.15) !important;
  transform: scale(1.1);
}

/* Zoom Controls */
.zoom-control-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  color: #3989C9 !important;
  border-radius: 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 4px rgba(57, 137, 201, 0.1) !important;
}

.zoom-control-btn:hover {
  background: rgba(57, 137, 201, 0.12) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: scale(1.08) !important;
  box-shadow: 0 4px 8px rgba(57, 137, 201, 0.2) !important;
}

.zoom-control-btn-danger {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(244, 67, 54, 0.2) !important;
  color: #F44336 !important;
  border-radius: 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 4px rgba(244, 67, 54, 0.1) !important;
}

.zoom-control-btn-danger:hover {
  background: rgba(244, 67, 54, 0.12) !important;
  border-color: rgba(244, 67, 54, 0.3) !important;
  transform: scale(1.08) !important;
  box-shadow: 0 4px 8px rgba(244, 67, 54, 0.2) !important;
}

.zoom-label {
  font-weight: 700 !important;
  color: rgba(57, 137, 201, 0.95) !important;
  min-width: 50px;
  text-align: center;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 4px 8px !important;
  background: rgba(57, 137, 201, 0.08) !important;
  border-radius: 6px !important;
}

/* Room Dialog Styling */
.room-dialog-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.room-dialog-title {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 50%,
    rgba(97, 84, 164, 0.08) 100%) !important;
  padding: 20px 24px !important;
}

.dialog-title-text {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: rgba(57, 137, 201, 0.95) !important;
  letter-spacing: 0.3px !important;
}

/* Room Input Fields */
.room-input >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.room-input:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.room-input.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.room-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
}

.room-input >>> label {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* Dialog Buttons */
.cancel-btn {
  background: rgba(120, 120, 130, 0.08) !important;
  color: rgba(70, 70, 80, 0.8) !important;
  border: 1px solid rgba(120, 120, 130, 0.2) !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 0 20px !important;
  transition: all 0.2s ease !important;
}

.cancel-btn:hover {
  background: rgba(120, 120, 130, 0.15) !important;
  border-color: rgba(120, 120, 130, 0.3) !important;
}

.save-btn {
  background: rgba(76, 175, 80, 0.12) !important;
  color: #4CAF50 !important;
  border: 1px solid rgba(76, 175, 80, 0.3) !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 0 20px !important;
  transition: all 0.2s ease !important;
}

.save-btn:hover:not(:disabled) {
  background: rgba(76, 175, 80, 0.2) !important;
  border-color: rgba(76, 175, 80, 0.5) !important;
  transform: translateY(-1px);
}

.save-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}
</style>
